import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { cartValue, track } from '@theme/r-analytics';

/**
 * The note as last typed in this tab, kept until the cart confirms it. A save lost to a
 * navigation inside the debounce, a failed request or a closed drawer comes back from here.
 */
const DRAFT_KEY = 'r-cart-note-draft';

/** @returns {string | null} */
function readDraft() {
  try {
    return sessionStorage.getItem(DRAFT_KEY);
  } catch {
    return null;
  }
}

/** @param {string | null} note - null clears the draft */
function writeDraft(note) {
  try {
    if (note === null) sessionStorage.removeItem(DRAFT_KEY);
    else sessionStorage.setItem(DRAFT_KEY, note);
  } catch {}
}

/**
 * The note is written out by hand, so it keeps only what a pen can write: letters and accents
 * in any script, digits, punctuation, spaces and line breaks, currency signs and the ASCII
 * symbols, hearts (♡ ♥ ❤ ❥) and smiley faces (☺ ☻). Emoji, pictographs, arrows and box
 * drawing go, and so do the variation selectors that would otherwise survive an emoji's removal
 * as invisible characters — which is also what turns a phone keyboard's ❤️ and ☺️ into the
 * plain ❤ and ☺ they are drawn from.
 */
const NOT_HANDWRITABLE = /[^\p{L}\p{Mn}\p{Mc}\p{N}\p{P}\p{Zs}\p{Sc}\n\r+<=>^`|~°⁄♡♥❤❥☺☻]/gu;
const VARIATION_SELECTORS = /[\uFE00-\uFE0F]/gu;

/** Phone keyboards offer hearts and smiles as emoji only, so the common ones become the drawable symbol. */
const HEART_EMOJI = /[💕💖💗💓💞💘💝🧡💛💚💙💜🤎🖤🤍🩷🩵🩶]/gu;
const SMILEY_EMOJI = /[🙂😊😀😃😄😁😆🥰😍]/gu;

/**
 * The text with everything that has a drawable form converted to it, nothing removed yet. NFKC
 * turns styled look-alikes into the letters they stand for (𝓗𝓲 → Hi, ① → 1, ﬁ → fi), so pasted
 * "fancy text" is kept rather than stripped.
 * @param {string} text
 */
function drawable(text) {
  return text
    .normalize('NFKC')
    .replace(HEART_EMOJI, '❤')
    .replace(SMILEY_EMOJI, '☺')
    .replace(VARIATION_SELECTORS, '');
}

/** @param {string} text */
function handwritable(text) {
  return drawable(text).replace(NOT_HANDWRITABLE, '');
}

// A page rendered with an empty cart means any draft belongs to a cart that is gone — most
// often one checked out with a wallet button, which never submits the cart form. Without
// this, its unsaved note would be restored into the shopper's next cart.
if (document.querySelector('.cart-drawer--empty, .cart-page--empty')) writeDraft(null);

/**
 * A custom element that displays a cart note.
 */
class CartNote extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {AbortController | null} */
  #listeners = null;

  /** Whether the last persisted note had content — so only add/remove transitions are tracked. */
  #hasNote = false;

  connectedCallback() {
    super.connectedCallback();

    const textarea = this.#textarea;
    this.#hasNote = Boolean(textarea?.value.trim());
    if (!textarea) return;

    // Checkout submits the note with the cart form, so the draft has done its job.
    this.#listeners = new AbortController();
    textarea.form?.addEventListener('submit', () => writeDraft(null), { signal: this.#listeners.signal });

    textarea.addEventListener('compositionend', () => this.#onCompositionEnd(textarea), {
      signal: this.#listeners.signal,
    });

    this.#restoreDraft(textarea);

    // A note saved before the rule existed, or by another surface: clean it and save it back.
    if (this.#keepHandwritable(textarea).changed) {
      writeDraft(textarea.value);
      this.#updateCount(textarea);
      this.#saveCartNote(textarea.value);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
  }

  get #textarea() {
    const textarea = this.querySelector('textarea');
    return textarea instanceof HTMLTextAreaElement ? textarea : null;
  }

  /**
   * Handles updates to the cart note: the draft and character count at once, the save debounced.
   * @param {InputEvent} event - The input event in our text-area.
   */
  updateCartNote = (event) => {
    if (!(event.target instanceof HTMLTextAreaElement)) return;
    // Mid-composition (IME) text is provisional; compositionend cleans the committed result.
    if (!event.isComposing && this.#keepHandwritable(event.target).removed) this.#showNotice();

    writeDraft(event.target.value);
    this.#updateCount(event.target);
    this.#saveCartNote(event.target.value, event);
  };

  /**
   * An IME can commit an emoji, and Chrome fires no input event after compositionend.
   * @param {HTMLTextAreaElement} textarea
   */
  #onCompositionEnd(textarea) {
    const { changed, removed } = this.#keepHandwritable(textarea);
    if (!changed) return;

    if (removed) this.#showNotice();
    writeDraft(textarea.value);
    this.#updateCount(textarea);
    this.#saveCartNote(textarea.value);
  }

  /**
   * Converts and strips what can't be handwritten, keeping the caret where it was in the text
   * that stays. A conversion alone (💕 → ❤, 𝓗𝓲 → Hi) changes the note and must be saved, but
   * only a removal is worth telling the shopper about.
   * @param {HTMLTextAreaElement} textarea
   * @returns {{ changed: boolean, removed: boolean }}
   */
  #keepHandwritable(textarea) {
    const { value, selectionEnd } = textarea;
    const limit = textarea.maxLength > 0 ? textarea.maxLength : undefined;
    const clean = handwritable(value).slice(0, limit);
    if (clean === value) return { changed: false, removed: false };

    const removed = clean !== drawable(value).slice(0, limit);
    textarea.value = clean;
    if (document.activeElement === textarea) {
      const caret = Math.min(handwritable(value.slice(0, selectionEnd)).length, clean.length);
      textarea.setSelectionRange(caret, caret);
    }
    return { changed: true, removed };
  }

  #showNotice() {
    const notice = this.refs.notice;
    if (notice instanceof HTMLElement && notice.dataset.message) notice.textContent = notice.dataset.message;
  }

  /**
   * Puts back a note typed in this tab that never reached the cart, opens the accordion on it
   * and saves it.
   * @param {HTMLTextAreaElement} textarea
   */
  #restoreDraft(textarea) {
    const draft = readDraft();
    if (draft === null) return;
    if (draft === textarea.value) {
      writeDraft(null);
      return;
    }

    textarea.value = draft.slice(0, textarea.maxLength > 0 ? textarea.maxLength : undefined);
    this.#updateCount(textarea);
    if (draft.trim()) this.#open();
    this.#saveCartNote(textarea.value);
  }

  /** Opens the accordion without its transition, and keeps it open across breakpoint changes. */
  #open() {
    const accordion = this.querySelector('accordion-custom');
    accordion?.setAttribute('open-by-default-on-desktop', '');
    accordion?.setAttribute('open-by-default-on-mobile', '');

    const details = this.querySelector('details');
    if (!details || details.open) return;

    details.classList.add('accordion--no-animation');
    details.open = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => details.classList.remove('accordion--no-animation'));
    });
  }

  /**
   * Rewrites the "N/160 characters used" line from its server-rendered template.
   * @param {HTMLTextAreaElement} textarea
   */
  #updateCount(textarea) {
    const count = this.refs.count;
    const template = count instanceof HTMLElement ? count.dataset.template : null;
    if (!count || !template) return;

    count.textContent = template
      .replace('[current]', String(textarea.value.length))
      .replace('[max]', String(textarea.maxLength));
  }

  /**
   * Persists the cart note.
   * @param {string} note
   * @param {InputEvent} [event] - The input event in our text-area, for the performance mark.
   */
  #saveCartNote = debounce(async (note, event) => {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ note }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });
      if (!response.ok) return;

      const cart = await response.json().catch(() => null);
      // Confirmed only if nothing newer was typed while this request was in flight.
      if ((cart?.note ?? '') === (readDraft() ?? '')) writeDraft(null);

      const hasNote = Boolean(note.trim());
      if (hasNote !== this.#hasNote) {
        this.#hasNote = hasNote;
        track('cart_extra', {
          action: hasNote ? 'gift_note_add' : 'gift_note_remove',
          cart_value: cartValue(cart?.items_subtotal_price),
        });
      }
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      if (event) cartPerformance.measureFromEvent('note-update:user-action', event);
    }
  }, 200);
}

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', CartNote);
}
